import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaBars, FaSpinner, FaTrash, FaExpand, FaGoogle } from 'react-icons/fa';
import { getAccountBriefs, getBattleCards, getObjectionHandling, deleteAccountBrief, deleteBattleCard, deleteObjectionHandling, getAccountBriefById, getBattleCardById, getObjectionHandlingById } from '../../../services/compass';
import googleDocsService from '../../../services/googleDocs';
import { usePlan } from '../../../contexts/PlanContext';
import { useToast } from '../../../contexts/ToastContext';
import './Compass.css';
import './Radar.css'; // Import Radar styles for error banner
import CompassEditor from './CompassEditor';

export default function Compass({ hideHeader = false, hideSectionTitle = false }) {
  const { planType } = usePlan();
  const { showSuccess, showError } = useToast();
  const hasGoogleDocsAccess = ['Individual', 'Team'].includes(planType);
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  
  // Get current view from URL
  const view = location.pathname.includes('/editor') ? 'editor' : 'Saved Notes';
  const [loading, setLoading] = useState(true);
  const [accountBriefs, setAccountBriefs] = useState([]);
  const [battleCards, setBattleCards] = useState([]);
  const [objections, setObjections] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [overlayDocument, setOverlayDocument] = useState(null); // { type, data }
  const [exportingDoc, setExportingDoc] = useState(null);

  const compassMenuItems = [
    { label: 'Saved Notes', value: 'Saved Notes' },
    { label: 'Editor', value: 'editor' }
  ];
  const [googleDocsConnected, setGoogleDocsConnected] = useState(null); // null = checking, true = connected, false = not connected
  const [dismissGoogleDocsError, setDismissGoogleDocsError] = useState(false);

  // Check Google Docs connection status
  useEffect(() => {
    const checkGoogleDocsConnection = async () => {
      if (!hasGoogleDocsAccess) {
        setGoogleDocsConnected(null);
        return;
      }
      try {
        const isConnected = await googleDocsService.getStatus();
        setGoogleDocsConnected(isConnected);
      } catch (error) {
        console.error('Error checking Google Docs connection:', error);
        setGoogleDocsConnected(false);
      }
    };
    checkGoogleDocsConnection();
  }, [hasGoogleDocsAccess]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [briefs, cards, handling] = await Promise.all([
          getAccountBriefs(1000),
          getBattleCards(1000),
          getObjectionHandling(1000)
        ]);
        setAccountBriefs(briefs);
        setBattleCards(cards);
        setObjections(handling);
        setProposals([]); // Initialize with empty array for now
      } catch (err) {
        console.error('Error fetching compass data:', err);
        showError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleMenuSelect = (option) => {
    if (option === 'Saved Notes') {
      navigate('saved-notes');
    } else if (option === 'editor') {
      navigate('editor');
    }
    setMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Overlay close handler
  useEffect(() => {
    if (!overlayDocument) return;
    function handleEsc(e) {
      if (e.key === 'Escape') setOverlayDocument(null);
    }
    function handleClick(e) {
      if (document.getElementById('doc-overlay') && !document.getElementById('doc-overlay').contains(e.target)) {
        setOverlayDocument(null);
      }
    }
    document.addEventListener('keydown', handleEsc);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [overlayDocument]);

  // Fetch full document details if needed (for Account Brief, Battle Card, Objection Handling)
  const handleViewDocument = async (type, item) => {
    try {
      let doc;
      if (type === 'Account Brief') {
        doc = await getAccountBriefById(item.id);
      } else if (type === 'Battle Card') {
        doc = await getBattleCardById(item.id);
      } else if (type === 'Objection Handling') {
        doc = await getObjectionHandlingById(item.id);
      } else if (type === 'Proposals') {
        // For now, just use the item directly since there's no backend support
        doc = item;
      } else {
        doc = item;
      }
      setOverlayDocument(doc);
    } catch (err) {
      showError('Failed to load document details.');
    }
  };

  // Delete handlers
  const handleDelete = async (type, id) => {
    setDeletingId(id);
    try {
      if (type === 'Account Brief') {
        await deleteAccountBrief(id);
        setAccountBriefs(prev => prev.filter(item => item.id !== id));
      } else if (type === 'Battle Card') {
        await deleteBattleCard(id);
        setBattleCards(prev => prev.filter(item => item.id !== id));
      } else if (type === 'Objection Handling') {
        await deleteObjectionHandling(id);
        setObjections(prev => prev.filter(item => item.id !== id));
      } else if (type === 'Proposals') {
        // For now, just remove from local state since there's no backend support
        setProposals(prev => prev.filter(item => item.id !== id));
      }
      showSuccess('Document deleted successfully');
    } catch (err) {
      showError('Failed to delete. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  // Export to Google Docs handler
  const handleExportToGoogleDocs = async (type, id) => {
    if (!hasGoogleDocsAccess) {
      showError('Google Docs export is only available for Individual and Team plans.');
      return;
    }

    // Check Google Docs connection before attempting export
    try {
      const isConnected = await googleDocsService.getStatus();
      if (!isConnected) {
        showError('Google Docs not connected. Please connect your Google account in Settings first.');
        setDismissGoogleDocsError(false); // Reset dismiss state to show error
        return;
      }
    } catch (error) {
      console.error('Error checking Google Docs connection:', error);
      showError('Failed to check Google Docs connection. Please try again.');
      return;
    }

    setExportingDoc(id);

    try {
      let documentType;
      switch (type) {
        case 'Account Brief':
          documentType = 'account_brief';
          break;
        case 'Battle Card':
          documentType = 'battle_card';
          break;
        case 'Objection Handling':
          documentType = 'objection_handling';
          break;
        case 'Proposals':
          documentType = 'proposals';
          break;
        default:
          throw new Error('Invalid document type');
      }

      const result = await googleDocsService.exportSavedNote(documentType, id);
      showSuccess(`Document exported to Google Docs successfully! Document ID: ${result.data.documentId}`);
    } catch (error) {
      console.error('Export error:', error);
      showError(error.response?.data?.message || 'Failed to export document to Google Docs');
    } finally {
      setExportingDoc(null);
    }
  };

  const renderCardContent = (items, type) => {
    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <FaSpinner className="spinner" />
        </div>
      );
    }

    if (!items || items.length === 0) {
      return <div className="empty-message">No {type.toLowerCase()} documents available</div>;
    }

    return (
      <div className="compass-card-board" style={{ maxHeight: 400, overflowY: 'auto' }}>
        {items.map((item) => {
          // Format date for display
          const dateStr = item.generatedAt ? new Date(item.generatedAt).toLocaleDateString() : '';
          return (
            <div key={item.id} className="compass-board-option" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, position: 'relative', height: '40px' }}>
              <div style={{ flex: 1 }}>
            {type === 'Account Brief' && (
              <>
                {item.companyName}
                    <span style={{ fontSize: '0.5rem' }}>{dateStr}</span>
              </>
            )}
            {type === 'Battle Card' && (
              <>
                {item.competitorName}
                    <span style={{ fontSize: '0.5rem' }}>{dateStr}</span>
              </>
            )}
            {type === 'Objection Handling' && (
              <>
                {item.objectionType}
                    <span style={{ fontSize: '0.5rem' }}>{dateStr}</span>
              </>
            )}
            {type === 'Proposals' && (
              <>
                {item.proposalTitle || 'Proposal'}
                    <span style={{ fontSize: '0.5rem' }}>{dateStr}</span>
              </>
            )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  className="view-btn"
                  onClick={() => handleViewDocument(type, item)}
                  style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 18 }}
                  title="View"
                >
                  <FaExpand />
                </button>
                {hasGoogleDocsAccess && (
                  <button
                    className="export-btn"
                    onClick={() => handleExportToGoogleDocs(type, item.id)}
                    disabled={exportingDoc === item.id}
                    style={{ background: 'none', border: 'none', color: '#34a853', cursor: 'pointer', fontSize: 18 }}
                    title="Export to Google Docs"
                  >
                    {exportingDoc === item.id ? <FaSpinner className="spinner" /> : <FaGoogle />}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Overlay rendering
  const renderOverlay = () => {
    if (!overlayDocument) return null;
    // Extract fields
    const id = overlayDocument.id;
    const documentType = overlayDocument.documentType;
    const companyName = overlayDocument.companyName || overlayDocument.competitorName || overlayDocument.objectionType || '';
    const createdAt = overlayDocument.createdAt || overlayDocument.generatedAt;
    const generatedContent = overlayDocument.generatedContent;
    // Format date
    const formattedDate = createdAt ? new Date(createdAt).toLocaleString() : '';
    // Format generatedContent as HTML if needed
    let htmlContent = '';
    if (generatedContent) {
      let docObj = generatedContent;
      if (typeof generatedContent === 'string') {
        try {
          docObj = JSON.parse(generatedContent);
        } catch {
          docObj = generatedContent;
        }
      }
      if (typeof docObj === 'object' && docObj !== null) {
        htmlContent = Object.entries(docObj)
          .map(([section, content]) => {
            let sectionContent = '';
            if (typeof content === 'object' && content !== null) {
              sectionContent = Object.entries(content)
                .map(([key, value]) => {
                  if (typeof value === 'string') {
                    return `<div>${value}</div>`;
                  } else {
                    return `<div>${JSON.stringify(value, null, 2)}</div>`;
                  }
                })
                .join('');
            } else if (typeof content === 'string') {
              sectionContent = `<div>${content}</div>`;
            } else {
              sectionContent = `<div>${JSON.stringify(content, null, 2)}</div>`;
            }
            return `<div style='margin-bottom: 1em; display: block;'><strong style='font-size:1.1em;'>${section}</strong>${sectionContent}</div>`;
          })
          .join('');
      } else {
        htmlContent = docObj;
      }
    }
    return (
      <div className="doc-overlay-bg" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div id="doc-overlay" style={{ background: '#fff', borderRadius: 12, maxWidth: 700, width: '95vw', maxHeight: '90vh', overflowY: 'auto', padding: 32, position: 'relative', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, color: '#888', marginBottom: 4 }}>ID: {id}</div>
            <h2 style={{ margin: 0, fontSize: 28 }}>{companyName}</h2>
            <div style={{ fontSize: 14, color: '#888', marginTop: 4 }}>Created: {formattedDate}</div>
          </div>
          <div style={{ borderTop: '1px solid #eee', margin: '16px 0 0 0', paddingTop: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Document:</div>
            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 16, minHeight: 120, maxHeight: 400, overflowY: 'auto', fontSize: 16, color: '#222', lineHeight: 1.7 }}
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          </div>
          {/* Delete button in overlay */}
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                handleDelete(documentType, id);
                setOverlayDocument(null);
              }}
              disabled={deletingId === id}
              style={{
                padding: '8px 16px',
                background: '#dc2626',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              title="Delete Document"
            >
              {deletingId === id ? <FaSpinner className="spinner" /> : <FaTrash />}
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ background: '#F7F9FC', minHeight: '100vh', width: '100%' }}>
      {/* Header only if not hidden */}
      {!hideHeader && (
        <div className="compass-header-row" style={{ background: 'linear-gradient(120deg, rgba(241, 245, 249, 1) 3%, rgba(219, 234, 254, 1) 37%, rgba(237, 233, 254, 1) 100%)', height: '6vh', padding: '20px 0px 10px 10px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', marginBottom: 24, alignItems: 'flex-start' }}>
          <div className="compass-header-left">
            <div style={{ fontSize: 24, fontWeight: 600, padding: 0, margin:0 }}>{view === 'editor' ? 'Editor' : 'Saved Notes'}</div>
          </div>
          <div className="compass-header-right" style={{ display: 'flex', gap: 8, paddingRight: 10 }}>
            <div className="compass-hamburger-wrapper" ref={menuRef}>
              <button className="compass-hamburger" onClick={() => setMenuOpen(!menuOpen)}>
                <FaBars />
              </button>
              {menuOpen && (
                <>
                  <div className="compass-menu-overlay" onClick={() => setMenuOpen(false)} />
                  <div className="compass-menu-dropdown">
                    {compassMenuItems.map((item) => {
                      const isActive = view === item.value;
                      return (
                        <div
                          key={item.value}
                          className={`compass-menu-item${isActive ? ' active' : ''}`}
                          onClick={() => handleMenuSelect(item.value)}
                        >
                          {item.label}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Main content, remove extra container if hideHeader is true */}
      <div className="compass-container" style={{ background: 'transparent', marginTop: 0, boxShadow: 'none', padding: 0 }}>
      {view === 'Saved Notes' && (
          <div className="compass-bottom-row" style={{ background: 'transparent', marginTop: 0 }}>
          <div className="compass-card">
            <div className="compass-card-header">
              Account Briefs
            </div>
            {renderCardContent(accountBriefs, 'Account Brief')}
          </div>
          <div className="compass-card">
            <div className="compass-card-header">
              Battle Cards
            </div>
            {renderCardContent(battleCards, 'Battle Card')}
          </div>
          <div className="compass-card">
            <div className="compass-card-header">
              Objection Handling
            </div>
            {renderCardContent(objections, 'Objection Handling')}
          </div>
          <div className="compass-card">
            <div className="compass-card-header">
              Proposals
            </div>
            {renderCardContent(proposals, 'Proposals')}
          </div>
        </div>
      )}
      {view === 'editor' && <CompassEditor />}
        {renderOverlay()}
      </div>
    </div>
  );
} 